<template>
  <UIDialog
    :is-open
    :z-index
    size="full"
    title="Organize Family Structure"
    @close="handleClose"
  >
    <div class="reorganize-tree">
      <div class="reorganize-tree__panel">
        <UIPreloader v-if="!isTreeInitialized" message="Loading tree..." class="reorganize-tree__preloader" />
        <UITree
          v-else-if="!isEmptyTree"
          :data="resultTree"
          :root="AnalyticsField.REPO_ROOT"
          :meta-fields
          :formatter="nameFormatter"
          sort-field="balance"
          unsearchable
        >
          <template #name="{ isRoot, name, path }">
            <draggable
              v-if="isRoot"
              :group="{ name: 'nodes', put: true, pull: false }"
              :model-value="[repoName]"
              tag="div"
              :itemKey="(element: string) => element"
            >
              <template #item="{ element }">{{  element }}</template>
            </draggable>
            <draggable
              v-else
              group="nodes"
              :model-value="[path]"
              tag="div"
              :animation="0"
              :itemKey="(element: string[]) => element.join('-')"
              @choose="handleChoose(path)"
              @change="handleChange($event, path)"
            >
              <template #header><div class="reorganize-tree__item-placeholder">{{ name }}</div></template>
              <template #item><div class="reorganize-tree__item-name">{{  name }}</div></template>
            </draggable>
          </template>
          <template #meta="{ isRoot, amount, path }">
            <AmountField
              v-if="!isRoot"
              :entry-settings
              :amount
              :dynamic="path.length > 2"
              @click="handleClickAmount(path)"
            />
          </template>
          <template #actions="{ item, isRoot, path }">
            <UIButton
              v-if="isRoot"
              label="Account"
              variant="secondary"
              size="xs"
              :leading="PlusIcon"
              @click="handleAddAccount"
            />
            <UIButton
              v-else-if="item.isNew"
              v-tooltip.top="'Delete'"
              variant="danger"
              fill="light"
              size="xs"
              :leading="TrashIcon"
              @click="handleDeleteNode(item.path)"
            />
          </template>
        </UITree>
        <div v-else class="reorganize-tree__panel-empty">
          <span>You just need to add at least one <br />asset to the tree</span>
          <UIButton
            label="Account"
            variant="secondary"
            size="sm"
            :leading="PlusIcon"
            @click="handleAddAccount"
          />
        </div>
      </div>
      <div class="reorganize-tree__content">
        <div class="reorganize-tree__transactions">
          <UIPreloader v-if="!isTreeInitialized" message="Loading tree..." class="reorganize-tree__preloader" />
          <ReorganizeTreeTransactions
            v-else
            ref="transactionsRef"
            :transactions="transactions as TransactionClass[]"
            @delete="handleDeleteTransaction"
          />
        </div>
        <div class="reorganize-tree__historic">
          <ReorganizeTreeHistoric />
        </div>
      </div>
      <UIPreloader v-if="isSaving" message="Saving tree..." overlay  />
    </div>
    <template #footer>
      <UIButtonsGroup>
        <UIButtonSave
          label="Save"
          :loading="isSaving"
          :disabled="!hasUnsaved || isSaving"
          @click="handleClickSave"
        />
        <UIButtonClose :disabled="isSaving" @click="handleClose" />
      </UIButtonsGroup>
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref, markRaw, onMounted, onBeforeUnmount, provide, useTemplateRef, watch } from 'vue'
import { cloneDeep, forOwn, get, isEqual, isPlainObject, orderBy, set, unset } from 'lodash'
import draggable from 'vuedraggable'

import { AnalyticsField, ModalClass, TransactionClass } from '@types'
import { ReorganizeTreeItem } from './utils/types'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { generateId } from '@/entities/utils/helpers'
import { getCurrentDate } from '@/helpers/dates'
import { handleCatchedError } from '@/helpers/common'
import { onCreateAsset } from '@/entities/transactions/components/utils/helpers'

import { useNotifications } from '@/plugins/notification'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetsStore } from '@/store/assets'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import ReorganizeTreeHistoric from './ReorganizeTreeHistoric.vue'
import ReorganizeTreeTransactions from './ReorganizeTreeTransactions.vue'
import { UIPreloader, UITree } from '@ui/core'
import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonsGroup, UIButtonSave } from '@ui/buttons'
import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'

import PrecisionForm from './components/PrecisionForm.vue'
import AmountField from './components/AmountField.vue'

type Props = {
  modal: ModalClass<any>
}

const props = defineProps<Props>()

const { warn, error, success } = useNotifications()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const assetsStore = useAssetsStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()
const transactionsSettingsStore = useTransactionsSettingsStore()
const transactionsStore = useTransactionsStore()

const metaFields = ['amount', 'balance', 'path', 'isNew']

const tree = ref<ReorganizeTreeItem>({})

const transactions = ref<TransactionClass[]>([])

const chosenPath = ref<string[]>()

const isTreeInitialized = ref(false)
const isSaving = ref(false)

const transactionsRef = useTemplateRef('transactionsRef')

const repositoryId = computed(() => repositoriesStore.getCurrentId)
const repoName = computed(() => repositoriesStore.getCurrentName)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const analyticsTree = computed(() => analyticsStore.module.tree.getList)

const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

const isEmptyTree = computed(() => !Object.keys(tree.value?.[AnalyticsField.REPO_ROOT] || {}).length)

const transactionsToStore = computed(() => transactions.value.filter(transaction => transaction.entry1().value?.account_id !== AnalyticsField.REPO_ROOT))
const hasUnsaved = computed(() => transactionsToStore.value.length)

const entrySettings = computed(() => transactionsSettingsStore.getList.find(item => item.name === 'add account')?.entries[0])

const accountTypes = computed(() => entrySettings.value?.account?.includes || [])
const assetTypes = computed(() => entrySettings.value?.asset?.includes || [])

const resultTree = computed(() => {
  const result = cloneDeep(tree.value)
  transactions.value.forEach(transaction => {
    const accountId = transaction.entry1().value?.account_id
    const assetId = transaction.entry1().value?.asset_id
    if (!accountId || !assetId) return
    const transactionAmount = transaction.entry1().value?.amount ?? 0
    const holderPaths = getHolderPaths(accountId)
    holderPaths.forEach(path => {
      const resultPath = [...path, assetId]
      const amount = get(result, resultPath)?.amount ?? 0
      const newAmount = amount + transactionAmount
      if (newAmount === 0) {
        unset(result, resultPath)
      } else {
        set(result, [...resultPath, 'amount'], newAmount)
      }
    })
  })
  return result
})

const paths = computed(() => {
  const res: string[][] = []
  const stack: Array<{ node: unknown; path: string[] }> = [{ node: resultTree.value, path: [] }]
  while (stack.length) {
    const { node, path } = stack.pop()!
    if (!isPlainObject(node)) continue
    forOwn(node, (value, key) => {
      const nextPath = [...path, key]
      stack.push({ node: value, path: nextPath })
      if (key === AnalyticsField.REPO_ROOT || metaFields.includes(key)) return
      res.push(nextPath)
    })
  }
  return res
})

const createTransactionInstance = (accountId: string, assetId: string, amount = 1) => {
  const instance = new TransactionClass({
    id: generateId(),
    date: analyticsDate.value,
    type: 'add account',
    description: '',
    entries: [
      {
        account_id: accountId,
        amount,
        asset_id: assetId,
      }
    ],
    repository_id: repositoryId.value,
  }, true, true)
  return instance
}

const handleClickAmount = (path: string[]) => {
  // get asset id and account id
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  checkPrecisionAmount(assetId)
}

const handleAddAccount = () => {
  const restrictions = accountTypes.value
  const instance = assetsBunchStore.createElement()
  onCreateAsset('', restrictions, instance, modalsStore, (assetId: string) => {
    const path = [AnalyticsField.REPO_ROOT, assetId]
    set(tree.value, path, {
      path,
      balance: Infinity,
      amount: 1,
      isNew: true,
    })
  }, true)
}

const handleChange = (event: any, path: string[]) => {
  if (event.added) {
    const assetId = event.added.element.at(-1)
    handleAddedNode([...path, assetId])
  } else if (event.removed) {
    handleRemovedNode(event.removed.element)
  }
}

const getActualAmount = (path: string[]) => {
  const nodeAmount = get(resultTree.value, path)?.amount ?? 0
  const originalAmount = get(tree.value, path)?.amount ?? 0
  return nodeAmount !== originalAmount ? nodeAmount - originalAmount : nodeAmount
}

const handleAddedNode = (path: string[]) => {
  if (!chosenPath.value) return
  // get asset id and account id
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  if (assetId === accountId) {
    error({ message: 'You cannot add a node to itself' })
    chosenPath.value = undefined
    return
  }
  // get amount from chosen path
  const amount = getActualAmount(chosenPath.value)
  // check if transaction for this node already exists and update it
  if (updateTransactionAmountIfExists(accountId, assetId, amount)) return
  // else add new transaction with amount from chosen path
  pushTransactionToList(accountId, assetId, amount)
}

const handleRemovedNode = (path: string[]) => {
  if (!chosenPath.value) return
  chosenPath.value = undefined
  const assetId = path.at(-1)
  const accountId = path.at(-2)
  if (!assetId || !accountId) {
    error({ message: 'Asset ID and account ID are required' })
    return
  }
  const amount = getActualAmount(path)
  const isNew = !!get(tree.value, [AnalyticsField.REPO_ROOT, assetId])?.isNew
  if (isNew) {
    deleteTransactionsByPair(accountId, assetId)
    if (accountId !== AnalyticsField.REPO_ROOT) {
      return
    }
  } else {
    // check if transaction for this node already exists and delete it
    if (updateTransactionAmountIfExists(accountId, assetId, -amount)) return
  }
  // else add transaction with negative amount
  pushTransactionToList(accountId, assetId, -amount)
}

const handleDeleteNode = (path: string[]) => {
  unset(tree.value, path)
}

const handleDeleteTransaction = (index: number) => {
  const result = transactions.value.splice(index, 1)
  revertIfNewAccount(result[0]?.entry1().value?.asset_id)
}

const revertIfNewAccount = (assetId: string | undefined | null) => {
  if (!assetId) return
  if (get(tree.value, [AnalyticsField.REPO_ROOT, assetId])?.isNew) {
    deleteTransactionsByPair(AnalyticsField.REPO_ROOT, assetId)
  }
}

const handleChoose = (path: string[]) => {
  chosenPath.value = path
}

const checkPrecisionAmount = (assetId: string) => {
  const amounts = findAmountsByAssetId(assetId)
  // open amount form
  const modalInstance = modalsStore.init('precision-form', markRaw(PrecisionForm))
  modalInstance?.open(modalsStore.getZIndex(), {
    entrySettings: entrySettings.value,
    amounts,
    assetId,
    paths: paths.value,
    onComplete: (data: [string[], number][]) => {
      data.forEach(([path, amount]) => {
        const assetId = path.at(-1)
        const accountId = path.at(-2)
        if (!assetId || !accountId) return
        const oldAmount = amounts.find(item => isEqual(item[0], path))?.[1] ?? 0
        if (oldAmount === amount) return
        const newAmount = amount - oldAmount
        const exists = findTransactionByPair(accountId, assetId)
        if (exists) {
          const transactionAmount = exists.entry1().value?.amount ?? 0
          const newTransactionAmount = newAmount + transactionAmount
          if (newTransactionAmount === 0) {
            transactions.value = transactions.value.filter(transaction => transaction.id !== exists.id)
          } else {
            exists.entry1().value.amount = newTransactionAmount
          }
        } else if (newAmount !== 0) {
          pushTransactionToList(accountId, assetId, newAmount)
        }
      })
    }
  })
}

const findTransactionByPair = (accountId: string, assetId: string) => {
  return transactions.value.find(transaction => transaction.entry1().value?.account_id === accountId && transaction.entry1().value?.asset_id === assetId)
}

const deleteTransactionsByPair = (accountId: string, assetId: string) => {
  transactions.value = transactions.value.filter(transaction => transaction.entry1().value?.account_id !== accountId || transaction.entry1().value?.asset_id !== assetId)
}

const updateTransactionAmountIfExists = (accountId: string, assetId: string, amount: number) => {
  const transaction = findTransactionByPair(accountId, assetId)
  if (!transaction) return amount === 0
  if (amount === 0) {
    transactions.value = transactions.value.filter(transaction => transaction.id !== transaction.id)
  } else {
    transaction.entry1().value.amount = amount
  }
  return true
}

const pushTransactionToList = (accountId: string, assetId: string, amount: number) => {
  const transactionInstance = createTransactionInstance(accountId, assetId, amount)
  if (!transactionInstance) return
  transactions.value.push(transactionInstance)
  transactionsRef.value?.scrollToBottom()
}

const getHolderPaths = (accountId: string) => {
  const res: string[][] = []
  const stack: Array<{ node: unknown; path: string[] }> = [{ node: resultTree.value, path: [] }]
  while (stack.length) {
    const { node, path } = stack.pop()!
    if (!isPlainObject(node)) continue
    forOwn(node, (value, key) => {
      const nextPath = [...path, key]
      if (key === accountId) {
        res.push(nextPath)
      }
      stack.push({ node: value, path: nextPath })
    })
  }
  return res
}

const findAmountsByAssetId = (assetId: string) => {
  const res: [string[], number][] = []
  const stack: Array<{ node: unknown; path: string[] }> = [{ node: resultTree.value, path: [] }]
  while (stack.length) {
    const { node, path } = stack.pop()!
    if (!isPlainObject(node)) continue
    forOwn(node, (value, key) => {
      const nextPath = [...path, key]
      if (key === assetId) {
        const amount = get(resultTree.value, nextPath)?.amount ?? 0
        res.push([nextPath, amount])
      }
      stack.push({ node: value, path: nextPath })
    })
  }
  return res
}

const nameFormatter = (key: string) => {
  const asset = assetsBunchStore.getElementById(key)
  return asset?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}
provide('nameFormatter', nameFormatter)

const handleClickSave = async () => {
  try {
    isSaving.value = true
    // store new transactions
    await transactionsStore.bulkStore(transactionsToStore.value.map(instance => instance.get()))
    // refresh data
    await Promise.all([assetsStore.fetch(), transactionsStore.fetch()])
    success({ message: 'Tree saved successfully' })
    props.modal.close()
  } catch (e) {
    if (!e) return
    const message = (e as Error).message
    error({ message })
    handleCatchedError(message)
  } finally {
    isSaving.value = false
  }
}

const handleClose = () => {
  if (!hasUnsaved.value) {
    props.modal.close()
    return
  }
  warn({
    message: 'Warning',
    description:
      'You have unsaved changes in the tree, would you like to continue editing?',
    actionRequired: true,
    actions: [
      {
        label: 'Continue editing',
        buttonVariant: 'primary',
        onClick: remove => {
          remove && remove()
        },
      },
      {
        label: 'Discard changes',
        buttonVariant: 'secondary',
        onClick: remove => {
          remove && remove()
          props.modal.close()
        },
      },
    ],
  })
}

watch(() => ({ data: analyticsTree.value, settingsInitFlag: transactionsSettingsStore.initFlag }), async ({ data, settingsInitFlag }) => {
  if (!data || !settingsInitFlag) return
  // shrink data to remove all leaf nodes
  const parentIds = new Set<string>()
  const orderedData = orderBy(data, [item => item.path.length, item => item.balance], ['asc', 'desc'])
  orderedData.forEach(item => {
    const parentId = item.path.at(-2)
    if (typeof parentId === 'string') {
      parentIds.add(parentId)
    }
  })
  // create tree structure with existing analytics tree
  orderedData.forEach(item => {
    const accountId = item.path.length > 2 ? item.path.at(-2) : AnalyticsField.REPO_ROOT
    const assetId = item.path.at(-1)
    if (!assetId || !accountId) return
    // check if asset is in types list
    const instance = assetsBunchStore.getElementById(assetId)
    if (!instance) return
    const assetType = instance.field<string>(ASSET_FIELD.TYPE).value
    if (assetType === 'other' && !parentIds.has(assetId)) return
    if (![...accountTypes.value, ...assetTypes.value].includes(assetType)) return
    set(tree.value, item.path, {
      path: item.path,
      balance: item.balance,
      amount: item.amount,
      isNew: false,
    })
  })
  isTreeInitialized.value = true
}, { immediate: true })

onMounted(async () => {
  analyticsStore.setActive('ReorganizeTree', ['tree'])
  if (!transactionsSettingsStore.initFlag) {
    await transactionsSettingsStore.fetch()
  }
})

onBeforeUnmount(() => {
  analyticsStore.setInActive('ReorganizeTree')
})
</script>

<style scoped>
.reorganize-tree {
  @apply min-h-96 flex-auto;
  @apply grid grid-cols-1 sm:grid-cols-[minmax(350px,35%)_auto] gap-4;
  @apply -mb-4 -mx-3 sm:mx-0;

  &__panel,
  &__transactions,
  &__historic {
    @apply p-2 sm:p-4;
    @apply border border-light-border;
    @apply bg-body-gray;
    @apply rounded-lg;
  }

  &__panel {
    @apply h-full min-h-[200px];
    @apply flex flex-col;

    :deep(.ui-tree__name) {
      @apply relative;
      @apply flex-auto;
    }

    :deep(.ui-tree__item) {
      @apply min-h-10;
      @apply justify-between;
      @apply pl-0.5 pr-1;
      @apply bg-body-gray hover:bg-body;

      &:has(.sortable-ghost) {
        @apply bg-indigo-50 dark:bg-gray-900;
      }

      &:has(.sortable-ghost) {
        .reorganize-tree__item-placeholder {
          @apply block;
        }
        .reorganize-tree__item-name {
          @apply hidden;
        }
      }
    }
  }

  &__item-placeholder {
    @apply hidden last:block;
    @apply last:opacity-50;
  }

  &__item-name {
    @apply cursor-move;
  }

  &__content {
    @apply min-h-[600px] sm:min-h-0;
    @apply flex flex-col gap-4;
    @apply overflow-hidden;
  }

  &__transactions {
    @apply h-1/2;
    @apply overflow-hidden;
  }

  &__historic {
    @apply h-1/2;
    @apply flex flex-col px-0 pb-0;
    @apply overflow-hidden;

    :deep(.ui-panel) {
      @apply px-2 sm:px-4 pb-4;
    }
  }

  &__panel-empty {
    @apply h-full;
    @apply flex flex-col items-center justify-center;
    @apply gap-4;

    span {
      @apply text-xs;
      @apply text-light-text;
      @apply text-center;
    }
  }

  &__preloader {
    @apply w-full h-full;
    @apply flex flex-col items-center justify-center;
  }
}
</style>
